import { Icon } from '@ellucian/ds-icons/lib';
import { Alert, TextField, Typography } from '@ellucian/react-design-system/core';
import { withStyles } from '@ellucian/react-design-system/core/styles';
import MenuItem from '@mui/material/MenuItem';
import {
    Paper,
    InputAdornment
} from '@ellucian/react-design-system/core';
import classnames from 'classnames';
import Downshift from 'downshift';
import get from 'lodash/get';
import * as React from 'react';
import isHtml from 'is-html';

import {
    borderRadiusMedium,
    borderWidthThin,
    fontSizeDefault,
    fontWeightNormal,
    paddingTextField,
    spacing40,
    spacing30,
    spacing20,
    widthInputFields,
    zIndexModal,
} from '@ellucian/react-design-system/core/styles/tokens';
import { FetchSuggestions, FetchSuggestionsFunc, Suggestion } from './FetchSuggestions';
import Preview, { ILookupPreviewElement } from './Preview';
import Popper from '@mui/material/Popper/Popper';

const styles = (theme) => ({
    container: {
        display: 'inline-block',
    },
    inputLabel: {
        maxWidth: '25rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    inputSearch: {
        padding: spacing40,
    },
    inputSearchWithLabel: {
        padding: paddingTextField,
    },
    inputStartAdornment: {
        marginLeft: '5px',
        marginRight: ['-5px', '!important'],
        marginTop: '12px',
        zIndex: 1,
    },
    loadingText: {
        marginLeft: spacing30,
    },
    menuItem: {
        '&.highlighted, &:hover': {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.grey[600],
        },
        '&.selected': {
            '&.highlighted, &:hover': {
                backgroundColor: theme.palette.ctaColor.hover,
                color: theme.palette.grey[100],
            },
            'backgroundColor': theme.palette.ctaColor.base,
            'color': theme.palette.grey[100],
        },
        'display': 'inline-flex',
        'fontSize': fontSizeDefault,
        'fontWeight': fontWeightNormal,
        'height': 'auto',
        'minHeight': '3.375rem',
        'overflow': 'unset',
        'padding': `0rem ${spacing40}`,
        'whiteSpace': 'normal',
        'width': '285px'
    },
    menuItemContainer: {
        '&.highlighted, &:hover': {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.grey[600],
        },
        '&.selected': {
            '&.highlighted, &:hover': {
                backgroundColor: theme.palette.ctaColor.hover,
                color: theme.palette.grey[100],
            },
            'backgroundColor': theme.palette.ctaColor.base,
            'color': theme.palette.grey[100],
        },
        '&:last-child': {
            borderBottom: 0,
        },
        'borderBottom': `solid ${borderWidthThin} ${theme.palette.grey[400]}`,
        'display': 'inline-flex',
        'width': '330px',
    },
    menuItemLoading: {
        '&.highlighted, &:hover': {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.grey[600],
        },
        '&.selected': {
            '&.highlighted, &:hover': {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.grey[100],
            },
            'backgroundColor': theme.palette.ctaColor.base,
            'color': theme.palette.grey[100],
        },
        '&:last-child': {
            borderBottom: 0,
        },
        'backgroundColor': theme.palette.grey[100],
        'borderBottom': `solid ${borderWidthThin} ${theme.palette.grey[400]}`,
        'color': theme.palette.grey[600],
        'cursor': 'default',
        'fontSize': fontSizeDefault,
        'fontWeight': fontWeightNormal,
        'height': 'auto',
        'minHeight': '3.375rem',
        'padding': `0rem ${spacing40}`,
        'whiteSpace': 'normal',
    },
    paper: {
        border: `solid ${borderWidthThin} ${theme.palette.ctaColor.base}`,
        borderBottomLeftRadius: borderRadiusMedium,
        borderBottomRightRadius: borderRadiusMedium,
        borderBottomWidth: borderWidthThin,
        borderTop: `solid ${borderWidthThin} ${theme.palette.grey[400]}`,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        boxShadow: 'none',
        marginTop: '-5px',
        maxHeight: '50vh',
        overflow: 'auto',
        overflowX: 'hidden',
        paddingBottom: spacing20,
    },
    popper: {
        marginLeft: '-1px',
        zIndex: zIndexModal,
    },
    popperShiftLeft: {
        left: '-16px!important',
    },
    previewTypography: {
        fontWeight: 'bold',
        overflow: 'hidden',
        clear: 'both',
    },
    previewDiv: {
        fontWeight: 'bold',
        fontSize: '0.875rem',
        fontFamily: 'Open Sans, Helvetica, Arial, sans-serif',
        lineHeight: '1.43',
        marginBottom: '16px',
    },
    templateLink: {
        float: 'right',
        fontSize: 'small',
        fontWeight: 'bold',
        marginBottom: '16px',
    },
    searchIcon: {
        color: 'black',
        cursor: 'context-menu'
    },
});

type MenuItemRenderProp = (item: any, selected?: boolean, index?: number) => JSX.Element;

interface IResources {
    error: string;
    loading: string;
    noResults: string;
}

interface ILookup {
    onAlertError?: (message: string, showDefault?: boolean, key?: string) => void;
    autoFocus?: boolean;
    children?: MenuItemRenderProp;
    classes: any;
    className?: string;
    setClear?: (callback: () => void) => void;
    setOpenMenu?: (callback: () => void) => void;
    debounceInterval?: number;
    disabled?: boolean;
    error?: boolean;
    fetchSuggestions: FetchSuggestionsFunc;
    helperText?: string;
    id?: string;
    inputStartAdornmentContent?: any;
    label?: string;
    minLength?: number;
    onBlur?: () => void;
    onClear?: () => void;
    onChange?: (value: any, type?: string) => void;
    placeholder?: string;
    required?: boolean;
    searchIconName?: string;
    selectedValue?: any;
    strings: IResources;
    suggestionToKey?: (suggestion?: any, idx?: number) => string;
    suggestionToString?: (suggestion: any) => string;
    suggestionToValue?: (suggestion: any) => any;
    modifiers?: any;
    disablePortal?: boolean;
    showPreviewInMenu?: boolean;
    showPreviewInField?: boolean;
    previewModifiers?: object;
    previewContainer?: any;
    previewIconName?: string;
    previewElementData?: (suggestion: any) => ILookupPreviewElement;
    previewDisablePortal?: boolean;
    previewPlacement?: any;
}

const random = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

const defaultSuggestionToKey = () => random();
const defaultSuggestionToString = (suggestion) => get(suggestion, 'label', '');
const defaultSuggestionToValue = (suggestion: any) => suggestion;
const defaultMenuItemRenderProp = (suggestionToString): MenuItemRenderProp => (suggestion) => suggestionToString(suggestion); // tslint:disable max-line-length

export class LookupInternal extends React.Component<ILookup, any> {
    public static defaultProps = {
        minLength: 3,
        strings: {
            error: 'An unexpected error has occurred.',
            loading: 'Loading',
            noResults: 'No results.'
        },
        suggestionToKey: defaultSuggestionToKey,
        suggestionToString: defaultSuggestionToString,
        suggestionToValue: defaultSuggestionToValue,
    };

    private popperNode;
    private currentText: string = '';
    private lastSuggestions: Suggestion[] = [];
    private myClearSelection: () => void = null;
    private myOpenMenu: () => void = null;

    public componentDidMount() {
        const {
            setClear,
            setOpenMenu,
        } = this.props;

        // If requested by our parent component, provide hook references to the internal functions so that
        // they can be controlled from an outer level.
        if (setClear) {
            setClear(this.clear.bind(this));
        }
        if (setOpenMenu) {
            setOpenMenu(this.openMenu.bind(this));
        }
    }

    public clear = () => {
        this.currentText = null;
        if (this.myClearSelection) {
            this.myClearSelection();
        }
        if (this.props.onClear) {
            this.props.onClear();
        }
    }

    public openMenu = () => {
        if (this.myOpenMenu) {
            this.myOpenMenu();
        }
    }

    public render() {
        const {
            autoFocus,
            onAlertError,
            classes,
            className,
            debounceInterval,
            disabled,
            error,
            fetchSuggestions,
            helperText,
            id,
            inputStartAdornmentContent,
            label,
            minLength,
            onBlur,
            onChange,
            placeholder,
            required,
            selectedValue: initialSelectedItem,
            strings,
            suggestionToKey,
            suggestionToString,
            suggestionToValue,
            modifiers,
            disablePortal,
            searchIconName = 'search',
            showPreviewInMenu,
            showPreviewInField,
            previewModifiers,
            previewContainer,
            previewDisablePortal,
            previewIconName,
            previewElementData,
            previewPlacement
        } = this.props;

        const children = this.props.children || defaultMenuItemRenderProp(suggestionToString);

        // if the selected item is null, but we know we have text in the input field, show the text
        let mySuggestionToString = suggestionToString;
        if (!initialSelectedItem && this.currentText && this.currentText.length > 0) {
            mySuggestionToString = () => this.currentText;
        }

        const containerClasses = classnames(classes.container, className);

        const self = this;

        const previewFieldElement = (previewElementData) ? this.buildPreviewElement(previewElementData(initialSelectedItem), true) : null;

        const inputStartAdornment = !inputStartAdornmentContent ? null : (
            <InputAdornment
                id={`${id}_InputStartAdornment`}
                position="start"
                className={classes.inputStartAdornment}
            >
                {inputStartAdornmentContent}
            </InputAdornment>
        );

        const hasText = !!this.currentText || (!!initialSelectedItem && !!mySuggestionToString(initialSelectedItem));

        return (
            <Downshift
                id={id}
                selectedItem={initialSelectedItem}
                itemToString={mySuggestionToString}
                onChange={(selectedItem) => {
                    if (onChange) {
                        onChange(selectedItem, 'change');
                    }
                }}
                onInputValueChange={(text) => {
                    // save current text internally so it can be re-rendered without consumer knowing or caring.
                    this.currentText = text;

                    // if a selected items text is not the same as current text, then the selected
                    // item should be cleared.
                    if (onChange && suggestionToString && suggestionToString(initialSelectedItem) !== text) {
                        onChange(null, 'change');
                    }
                }}
            >
                {({
                    clearSelection,
                    closeMenu,
                    getInputProps,
                    getLabelProps,
                    getItemProps,
                    getMenuProps,
                    inputValue,
                    isOpen,
                    highlightedIndex,
                    openMenu,
                    selectedItem,
                }) => (
                        <div className={containerClasses}>
                            {this.setMyClearSelection(clearSelection)}
                            {this.setMyOpenMenu(openMenu)}
                            <TextField
                                InputProps={{
                                    inputRef: (node) => {
                                        this.popperNode = node;
                                    },
                                    ...getInputProps({ disabled }),
                                    endAdornment:
                                        <InputAdornment position="end" disablePointerEvents={disabled}>
                                            {!hasText &&
                                                <Icon
                                                    id={`${random()}_LookupInputSearchIcon`}
                                                    className={classes.searchIcon}
                                                    name={searchIconName}
                                                    onClick={() => {
                                                        this.popperNode.focus();
                                                        openMenu();
                                                    }}
                                                />
                                            }
                                            {hasText && previewFieldElement && showPreviewInField &&
                                                <Preview
                                                    id={`${random()}_LookupPreviewInFieldIcon`}
                                                    data-ui="input-preview"
                                                    iconName={previewIconName}
                                                    element={previewFieldElement}
                                                    previewInField={true}
                                                />
                                            }
                                            {hasText && (!previewFieldElement || !showPreviewInField) && !disabled &&
                                                <Icon
                                                    id={`${random()}_LookupInputClearIcon`}
                                                    className={classes.searchIcon}
                                                    name="close"
                                                    onClick={() => this.clear()}
                                                />
                                            }
                                        </InputAdornment>,
                                    startAdornment: inputStartAdornment,
                                    // tslint:disable-next-line:object-literal-sort-keys
                                    autoComplete: 'off'
                                }}
                                InputLabelProps={{
                                    className: classes.inputLabel,
                                    ...getLabelProps({ disabled }),
                                    ...this.getAttributeLabelProps(),
                                }}
                                {...getInputProps({
                                    disabled,
                                    onKeyDown: (e) => {
                                        // on TAB, if we have only 1 suggestion, select it automatically
                                        if (e.keyCode === 9) {
                                            if (self.lastSuggestions &&
                                                self.lastSuggestions.length === 1 &&
                                                self.props.onChange &&
                                                isOpen) {
                                                self.props.onChange(self.lastSuggestions[0], 'blur');
                                            }
                                        }
                                    }
                                } as any)
                                }
                                inputProps={{
                                    className: classnames(classes.inputSearch, {
                                        [classes.inputSearchWithLabel]: !!label
                                    }),
                                    role: 'presentation'
                                }} // turns autocomplete off in conjunction with autoComplete: 'off'
                                label={label}
                                required={required}
                                error={error}
                                helperText={helperText}
                                onBlur={() => {
                                    closeMenu();
                                    // if we have more than one and nothing selected, validate input on blur
                                    if (self.props.onChange && !selectedItem) {
                                        self.props.onChange(null, 'blur');
                                    }
                                    if (onBlur) {
                                        onBlur();
                                    }
                                }}
                                placeholder={placeholder}
                                type="text"
                                autoFocus={autoFocus}
                            />

                            <div {...getMenuProps()}>
                                <Popper
                                    className={`${classes.popper} ${!!inputStartAdornmentContent ? classes.popperShiftLeft : ''}`}
                                    disablePortal={(disablePortal) ? disablePortal : false}
                                    open={isOpen && inputValue.length >= minLength}
                                    anchorEl={this.popperNode}
                                    placement="bottom-start"
                                    modifiers={(modifiers) ? modifiers : null}
                                >
                                    {() => (
                                        <FetchSuggestions
                                            debounceInterval={debounceInterval}
                                            fetchSuggestions={fetchSuggestions}
                                            minLength={minLength}
                                            searchValue={inputValue}
                                        >
                                            {({ loading, suggestions, error: fetchError }) => {
                                                this.lastSuggestions = suggestions; // store in case needed

                                                if (loading) {
                                                    return (
                                                        <Paper
                                                            data-ui="menu"
                                                            className={classes.paper}
                                                            style={{
                                                                maxWidth: widthInputFields,
                                                                width: widthInputFields,
                                                            }}
                                                        >
                                                            <MenuItem
                                                                className={classes.menuItemLoading}
                                                                key="loading-menu-item"
                                                                selected={false}
                                                                component="div"
                                                                data-ui="menu-item"
                                                            >
                                                                <Icon name="spinner" spin={true} />
                                                                <div className={classes.loadingText}>
                                                                    {strings.loading}
                                                                </div>
                                                            </MenuItem>
                                                        </Paper>
                                                    );
                                                }

                                                if (fetchError) {
                                                    if (onAlertError) {
                                                        onAlertError(fetchError)
                                                    } else {
                                                        return (
                                                            <Paper
                                                                data-ui="menu"
                                                                className={classes.paper}
                                                                style={{
                                                                    width: `600px`
                                                                }}
                                                            >
                                                                <Alert
                                                                    data-ui="alert"
                                                                    alertType="error"
                                                                    open={true}
                                                                    text={strings.error}
                                                                    variant="inline"
                                                                />
                                                            </Paper>
                                                        );
                                                    }
                                                }

                                                if (!suggestions || suggestions.length === 0) {
                                                    return (
                                                        <Paper
                                                            data-ui="menu"
                                                            className={classes.paper}
                                                            style={{
                                                                maxWidth: widthInputFields,
                                                                width: widthInputFields,
                                                            }}
                                                        >
                                                            <MenuItem
                                                                className={classes.menuItemLoading}
                                                                key="no-results-menu-item"
                                                                selected={false}
                                                                component="div"
                                                                data-ui="menu-item"
                                                            >
                                                                <div className={classes.loadingText}>
                                                                    {strings.noResults}
                                                                </div>
                                                            </MenuItem>
                                                        </Paper>
                                                    );
                                                }

                                                if (suggestions && suggestions.length > 0) {
                                                    return (
                                                        <Paper
                                                            data-ui="menu"
                                                            className={classes.paper}
                                                            style={{
                                                                maxWidth: widthInputFields,
                                                                width: widthInputFields,
                                                            }}
                                                        >
                                                            {suggestions.map((suggestion, idx) => {
                                                                const highlighted = highlightedIndex === idx;

                                                                const selected =
                                                                    suggestionToValue(selectedItem) ===
                                                                    suggestionToValue(suggestion);

                                                                const itemProps = getItemProps({
                                                                    className: classnames(
                                                                        classes.menuItemContainer,
                                                                        {
                                                                            highlighted,
                                                                            selected,
                                                                        }
                                                                    ),
                                                                    item: suggestion
                                                                });

                                                                const previewMenuElement = (previewElementData) ? this.buildPreviewElement(previewElementData(suggestion), false) : null;

                                                                return (
                                                                    <div
                                                                        {...itemProps}
                                                                        data-ui="menu-item-container"
                                                                        key={suggestionToKey(suggestion, idx) + '_LookupContainer'}
                                                                    >
                                                                        <MenuItem
                                                                            className={classes.menuItem}
                                                                            key={suggestionToKey(suggestion, idx)}
                                                                            selected={selected}
                                                                            component="div"
                                                                            data-ui="menu-item"
                                                                        >
                                                                            {children(suggestion, selected, idx)}
                                                                        </MenuItem>
                                                                        {previewMenuElement && showPreviewInMenu &&
                                                                            <Preview
                                                                                key={suggestionToKey(suggestion, idx) + '_Preview'}
                                                                                modifiers={previewModifiers}
                                                                                container={previewContainer}
                                                                                disablePortal={previewDisablePortal}
                                                                                iconName={previewIconName}
                                                                                element={previewMenuElement}
                                                                                placement={previewPlacement}
                                                                                previewInField={false}
                                                                            />
                                                                        }
                                                                    </div>
                                                                );
                                                            })}
                                                        </Paper>
                                                    );
                                                }

                                                return null;
                                            }}
                                        </FetchSuggestions>
                                    )}
                                </Popper>
                            </div>
                        </div>
                    )}
            </Downshift>
        );
    }
    private getAttributeLabelProps(): any {
        if (!!this.props.inputStartAdornmentContent) {
            return {
                shrink: true
            };
        }

        return {};
    }
    private setMyClearSelection(clearSelection) {
        this.myClearSelection = clearSelection;
        return null;
    }
    private setMyOpenMenu(openMenu) {
        this.myOpenMenu = openMenu;
        return null;
    }
    private buildPreviewElement(data: ILookupPreviewElement, includeLink: boolean) {
        if (!data) {
            return null;
        }
        const previewElement = [];
        const { classes } = this.props;
        if (data.templateLink && includeLink) {
            previewElement.push(
                <div
                    className={classes.templateLink}>
                    <a
                        key={random() + data.templateLink.label}
                        href={'#'}
                        onClick={data.templateLink.onClick}
                    >
                        <span>{data.templateLink.label}</span>
                    </a>
                </div>
            );
        }
        if (data.header) {
            previewElement.push(
                <Typography
                    className={classes.previewTypography}
                    color="textPrimary"
                    variant="body2"
                    paragraph={true}
                    key={random() + data.header}
                >
                    {data.header}
                </Typography>
            );
        }
        data.body.forEach((element) => {
            if (element.value) {
                previewElement.push(
                    <React.Fragment key={random() + '_Fragment'}>
                        <Typography
                            color="textSecondary"
                            variant="body3"
                        >
                            {element.label}
                        </Typography>
                        {this.getElement(element.value)}
                    </React.Fragment>
                );
            }
        });

        return (previewElement.length > 0) ? previewElement : null;
    }

    private getElement(value: any): JSX.Element {
        const { classes } = this.props;
        return (
            isHtml && !isHtml(value) ?
                <Typography
                    className={classes.previewTypography}
                    color="textPrimary"
                    variant="body2"
                    paragraph={true}
                >
                    {value}
                </Typography>
                :
                <div className={classes.previewDiv}>
                    {value}
                </div>
        )
    }
}

export const Lookup = [
    withStyles(styles),
].reduce((memo, hoc: (it: any) => any) => hoc(memo), LookupInternal);

export default Lookup;
